import { action, computed, observable, makeObservable } from 'mobx';
import type {
    DispatchAreaGroupObject,
    DispatchAreaObject,
    ManageDispatchAreasView,
} from '@mark43/cad-api';

export class DispatchAreaStore {
    /**
     * All dispatch areas which are monitored or dispatched by the current user
     */
    @observable
    public readonly state: { manageDispatchAreasView?: ManageDispatchAreasView } = {
        manageDispatchAreasView: undefined,
    };

    constructor(initialManageDispatchAreasView?: ManageDispatchAreasView) {
        if (initialManageDispatchAreasView) {
            this.setManageDispatchAreasView(initialManageDispatchAreasView);
        }
        makeObservable(this);
    }

    @computed
    get ownedDispatchAreasById(): Record<number, DispatchAreaObject> {
        const ownedDispatchAreasMap: Record<number, DispatchAreaObject> = {};
        if (this.state.manageDispatchAreasView) {
            this.state.manageDispatchAreasView.userDispatchAreaObjects
                .filter(
                    (userDispatchAreaObject) =>
                        userDispatchAreaObject.dispatchAreaUserType === 'OWNER'
                )
                .forEach((userDispatchAreaObject) => {
                    ownedDispatchAreasMap[userDispatchAreaObject.id] = userDispatchAreaObject;
                });
        }
        return ownedDispatchAreasMap;
    }

    @computed
    get allDispatchAreaObjects(): DispatchAreaObject[] {
        if (!this.state.manageDispatchAreasView) {
            return [];
        }
        return this.state.manageDispatchAreasView?.allDispatchAreaObjects;
    }

    @computed
    get allDispatchAreaGroupObjects(): DispatchAreaGroupObject[] {
        if (!this.state.manageDispatchAreasView?.allDispatchAreaGroupObjects) {
            return [];
        }
        return this.state.manageDispatchAreasView.allDispatchAreaGroupObjects;
    }

    @computed
    get monitoredAndOwnedDispatchAreaObjects(): DispatchAreaObject[] {
        if (!this.state.manageDispatchAreasView) {
            return [];
        }
        return this.state.manageDispatchAreasView?.userDispatchAreaObjects;
    }

    @computed
    get monitoredAndOwnedDispatchAreaIds(): number[] {
        if (!this.state.manageDispatchAreasView) {
            return [];
        }
        return this.monitoredAndOwnedDispatchAreaObjects.map((object) => object.id);
    }

    public getMonitoredAndOwnedDispatchAreaIds(): number[] {
        if (!this.state.manageDispatchAreasView) {
            return [];
        }
        return this.monitoredAndOwnedDispatchAreaObjects.map((object) => object.id);
    }

    @action
    setManageDispatchAreasView(manageDispatchAreasView: ManageDispatchAreasView): void {
        this.state.manageDispatchAreasView = manageDispatchAreasView;
    }
}
